'use client';

import { useRef, useId, useState, useEffect, ReactNode, useMemo, useEffectEvent } from 'react';
import styles from './video.module.scss';
import cn from 'classnames';
import { Text } from '@/ui-components/atoms/Text/Text';
import { IconVideoPlayButton } from '@/assets/icons/IconVideoPlayButton';
import { IconVideoPauseButton } from '@/assets/icons/IconVideoPauseButton';
import { Transcript } from '@/ui-components/atoms/Transcript/Transcript';
import { ButtonWithLabel } from '@/ui-components/atoms/ButtonWithLabel/ButtonWithLabel';
import { IconHighVolume } from '@/assets/icons/IconHighVolume';
import { IconTranscript } from '@/assets/icons/IconTranscript';
import { IconTranscriptActive } from '@/assets/icons/IconTranscriptActive';
import { IconVolume } from '@/assets/icons/IconVolume';
import { IconDesktopView } from '@/assets/icons/IconDesktopView';
import { IconMutedVolume } from '@/assets/icons/IconMutedVolume';
import { useYouTubeApiLoader } from '@/hooks/useYouTubeApiLoader';
import { VideoSpinnerLoader } from '../VideoSpinnerLoader/VideoSpinnerLoader';
import { IconClosedCaption } from '@/assets/icons/IconClosedCaption';
import { IconClosedCaptionActive } from '@/assets/icons/IconClosedCaptionActive';
import { useConsent } from '@/hooks/useConsent';

const MAX_BUFFERING_TIME_MS = 3000;
const SESSION_CC_KEY = 'ccEnabled';

export interface VideoModalProps {
    poster?: string;
    source?: string;
    title?: ReactNode;
    description?: ReactNode;
    plainTextTitle?: string;
    videoId?: string;
    initiatePlayer?: boolean;
    className?: string;
    activeVideo?: boolean;
    transcript?: ReactNode;
    resumeFromLastTime?: boolean;
    defaultTranscriptOpen?: boolean;
    onTranscriptToggle?: (open: boolean) => void;
    onTimeSync?: (time: number) => void;
    syncTime?: number;
    isModalVideo?: boolean;
    isInlineVideo?: boolean;
    dataComponentType?: string;
    fullSizePoster?: boolean;
    captionSrc?: string;
    autoplay?: boolean;
    onPlayingChange?: (isPlaying: boolean) => void;
}
interface SafariVideoElement extends HTMLVideoElement {
    webkitEnterFullscreen?: () => void;
}
interface YouTubePlayer {
    playVideo: () => void;
    pauseVideo: () => void;
    seekTo: (seconds: number, allowSeekAhead: boolean) => void;
    getCurrentTime: () => number;
    getDuration: () => number;
    getPlayerState: () => number;
    destroy: () => void;
}
interface YouTubePlayerEvent {
    target: YouTubePlayer;
    data: number;
}
interface YouTubeAPI {
    Player: new (elementId: string | HTMLElement, config: YouTubePlayerConfig) => YouTubePlayer;
    PlayerState: {
        UNSTARTED: number;
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
    };
}
interface YouTubePlayerConfig {
    host?: string;
    height: string;
    width: string;
    videoId: string;
    playerVars: {
        autoplay: number;
        start?: number;
        rel: number;
        modestbranding: number;
        controls: number;
        playsinline: number;
        origin: string;
    };
    events: {
        onReady: (event: YouTubePlayerEvent) => void;
        onStateChange: (event: YouTubePlayerEvent) => void;
    };
}
declare global {
    interface Window {
        YT: YouTubeAPI;
        onYouTubeIframeAPIReady?: () => void;
    }
}
export function VideoPlayer(props: VideoModalProps): React.JSX.Element {
    const isYoutubeReady = useYouTubeApiLoader();
    const consentGranted = useConsent();
    const storageKey = useMemo(() => {
        return props.videoId
            ? `video-time-id-${props.videoId}`
            : props.source
              ? `video-time-src-${encodeURIComponent(props.source)}`
              : '';
    }, [props.videoId, props.source]);
    const videoRef = useRef<HTMLVideoElement>(null);
    const youtubePlayerRef = useRef<HTMLIFrameElement | null>(null);
    const youtubeContainerRef = useRef<HTMLDivElement | null>(null);
    const youtubeApiPlayerRef = useRef<YouTubePlayer | null>(null);
    const generatedId = useId();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isInitiated, setIsInitiated] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const youtubeLatestTime = useRef(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.5);
    const [isControlBarOpen, setControlBarOpen] = useState(false);
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
    useEffect(() => {
        setIsTranscriptOpen(props.defaultTranscriptOpen ?? false);
    }, [props.defaultTranscriptOpen]);
    const [previousVolumeLevel, setPreviousVolumeLevel] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const hasCompletedInitialBufferRef = useRef(false);
    const initialPlayFallbackTimeout = useRef<NodeJS.Timeout | null>(null);
    const videoModalClassNames = cn(styles.modalVideoContainer, props.className, {
        [styles.staticControlBar]: isControlBarOpen,
    });
    const youtubeVideoModalClassNames = cn(styles.youtubeVideo, props.className);
    const isSourceVideo = props.source?.includes('.mp4');
    const userPausedRef = useRef(false);
    const { onPlayingChange } = props;
    const shouldAutoplay = props.autoplay ?? true;
    const getCurrentYouTubeTime = () => {
        try {
            const currentTime = youtubeApiPlayerRef.current?.getCurrentTime?.();
            if (typeof currentTime === 'number' && !Number.isNaN(currentTime) && currentTime > 0) {
                youtubeLatestTime.current = currentTime;
                return currentTime;
            }
        } catch (error) {
            console.error('Error reading YouTube current time:', error);
        }
        return youtubeLatestTime.current;
    };

    const pushCurrentYouTubeTime = useEffectEvent(() => {
        const currentTime = getCurrentYouTubeTime();
        if (currentTime > 0) {
            props.onTimeSync?.(currentTime);
        }
    });

    const saveCurrentYouTubeTime = useEffectEvent(() => {
        if (props.videoId && storageKey) {
            try {
                const currentTime = getCurrentYouTubeTime();
                if (currentTime > 0) {
                    localStorage?.setItem(storageKey, String(currentTime));
                }
            } catch (error) {
                console.error('Error saving YouTube time:', error);
            }
        }
    });

    const emitHostedTimeSync = useEffectEvent(() => {
        if (videoRef.current) {
            props.onTimeSync?.(videoRef.current.currentTime);
        }
    });

    useEffect(() => {
        if (
            !props.videoId ||
            !props.initiatePlayer ||
            !props.activeVideo ||
            !isYoutubeReady ||
            !youtubeContainerRef.current
        ) {
            return;
        }
        if (youtubeApiPlayerRef.current) {
            try {
                youtubeApiPlayerRef.current.destroy?.();
            } catch (err) {
                console.error('[VideoPlayer] error destroying player', err);
            }
            youtubeApiPlayerRef.current = null;
            youtubePlayerRef.current = null;
        }
        const propStartTime = props.syncTime ?? 0;
        const savedTime = !propStartTime && storageKey ? localStorage.getItem(storageKey) : null;
        const startTime =
            propStartTime > 0 ? Math.floor(propStartTime) : savedTime ? Math.floor(parseFloat(savedTime)) : 0;
        try {
            youtubeApiPlayerRef.current = new window.YT.Player(youtubeContainerRef.current, {
                host: consentGranted ? 'https://www.youtube.com' : 'https://www.youtube-nocookie.com',
                height: '100%',
                width: '100%',
                videoId: props.videoId,
                playerVars: {
                    autoplay: shouldAutoplay ? 1 : 0,
                    ...(startTime > 0 ? { start: startTime } : {}),
                    rel: 0,
                    modestbranding: 1,
                    controls: 1,
                    playsinline: 1,
                    origin: typeof window === 'undefined' ? '' : window.location.origin,
                },
                events: {
                    onReady: (event) => {
                        youtubePlayerRef.current =
                            (youtubeContainerRef.current?.querySelector('iframe') as HTMLIFrameElement) ?? null;
                        youtubeLatestTime.current = startTime;
                        setIsLoading(false);
                        if (startTime > 0) {
                            event.target.seekTo(startTime, true);
                        }
                        if (shouldAutoplay && !userPausedRef.current) {
                            event.target.playVideo();
                        } else {
                            event.target.pauseVideo();
                            setIsPlaying(false);
                        }
                    },
                    onStateChange: (event) => {
                        const playerState = event.data;
                        if (playerState === window.YT.PlayerState.PLAYING) {
                            setIsPlaying(true);
                        } else if (playerState === window.YT.PlayerState.PAUSED) {
                            setIsPlaying(false);
                            pushCurrentYouTubeTime();
                            saveCurrentYouTubeTime();
                        } else if (playerState === window.YT.PlayerState.ENDED) {
                            setIsPlaying(false);
                            youtubeLatestTime.current = 0;
                            if (storageKey) {
                                localStorage?.removeItem(storageKey);
                            }
                        }
                    },
                },
            });
        } catch (error) {
            console.error('Error attaching YT.Player:', error);
        }
        return () => {
            pushCurrentYouTubeTime();
            saveCurrentYouTubeTime();
            try {
                youtubeApiPlayerRef.current?.destroy?.();
            } catch (error) {
                console.error('Error destroying YT.Player:', error);
            } finally {
                youtubeApiPlayerRef.current = null;
                youtubePlayerRef.current = null;
            }
        };
    }, [
        props.videoId,
        props.initiatePlayer,
        props.activeVideo,
        props.syncTime,
        storageKey,
        isYoutubeReady,
        shouldAutoplay,
        consentGranted,
    ]);
    useEffect(() => {
        if (videoRef.current?.currentTime !== undefined && props.syncTime !== undefined) {
            videoRef.current.currentTime = props.syncTime;
        }
    }, [props.syncTime]);
    useEffect(() => {
        let syncInterval: NodeJS.Timeout;
        if (isPlaying && props.onTimeSync && !props.videoId) {
            syncInterval = setInterval(() => {
                emitHostedTimeSync();
            }, 1000);
        }
        return () => {
            if (syncInterval) {
                clearInterval(syncInterval);
            }
        };
    }, [isPlaying, props.onTimeSync, props.videoId]);

    const clearInitialPlayFallbackTimeout = () => {
        if (initialPlayFallbackTimeout.current) {
            clearTimeout(initialPlayFallbackTimeout.current);
        }
    };
    useEffect(() => {
        const videoElement = videoRef.current;
        if (props.initiatePlayer && props.activeVideo && videoElement && isSourceVideo) {
            const startPlayback = () => {
                if (!shouldAutoplay || userPausedRef.current) {
                    setIsInitiated(true);
                    setIsLoading(false);
                    setIsPlaying(false);
                    return;
                }
                videoElement.play();
                setIsPlaying(true);
                setIsInitiated(true);
                setIsLoading(false);
            };
            const handleCanPlay = () => {
                if (hasCompletedInitialBufferRef.current) {
                    startPlayback();
                }
            };
            const handleLoadStart = () => {
                setIsLoading(true);
            };
            const handleWaiting = () => {
                setIsLoading(true);
            };
            const handleCanPlayFallback = () => {
                videoElement.removeEventListener('canplay', handleCanPlayFallback);
                clearInitialPlayFallbackTimeout();
                startPlayback();
            };
            const handleCanPlayThrough = () => {
                hasCompletedInitialBufferRef.current = true;
                clearInitialPlayFallbackTimeout();
                startPlayback();
            };
            videoElement.addEventListener('canplay', handleCanPlay);
            videoElement.addEventListener('loadstart', handleLoadStart);
            videoElement.addEventListener('waiting', handleWaiting);
            videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
            initialPlayFallbackTimeout.current = setTimeout(() => {
                if (!shouldAutoplay || userPausedRef.current) {
                    setIsInitiated(true);
                    setIsLoading(false);
                    setIsPlaying(false);
                    return;
                }
                if (videoElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
                    startPlayback();
                } else {
                    videoElement.addEventListener('canplay', handleCanPlayFallback);
                }
            }, MAX_BUFFERING_TIME_MS);
            videoElement.load();
            return () => {
                userPausedRef.current = false;
                videoElement?.removeEventListener('canplay', handleCanPlay);
                videoElement?.removeEventListener('loadstart', handleLoadStart);
                videoElement?.removeEventListener('waiting', handleWaiting);
                videoElement?.removeEventListener('canplaythrough', handleCanPlayThrough);
                videoElement?.removeEventListener('canplay', handleCanPlayFallback);
                clearInitialPlayFallbackTimeout();
                videoElement?.pause();
                setIsPlaying(false);
                setIsInitiated(false);
                setIsLoading(false);
            };
        } else if (videoElement) {
            videoElement.pause();
            videoElement.currentTime = 0;
            setIsPlaying(false);
            setIsLoading(false);
        }
    }, [props.initiatePlayer, props.activeVideo, props.videoId, isSourceVideo, shouldAutoplay]);
    // Remove the localstorage.clear(), it already have per-video cleanup that removes the saved time key.
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;
        if (props.resumeFromLastTime && storageKey) {
            const handleLoadedMetadata = () => {
                setDuration(videoElement.duration);
                const savedTime = localStorage.getItem(storageKey);
                if (savedTime) {
                    const parsedTime = parseFloat(savedTime);
                    videoElement.currentTime = parsedTime;
                    setCurrentTime(parsedTime);
                }
            };
            const handleTimeUpdate = () => {
                setCurrentTime(videoElement.currentTime);
            };
            const handleBeforeUnload = () => {
                if (storageKey && videoElement.currentTime > 0) {
                    localStorage.setItem(storageKey, String(videoElement.currentTime));
                }
            };
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
            videoElement.addEventListener('timeupdate', handleTimeUpdate);
            const saveInterval = setInterval(() => {
                if (storageKey && videoElement.currentTime > 0) {
                    localStorage.setItem(storageKey, String(videoElement.currentTime));
                }
            }, 5000);
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
                window.removeEventListener('beforeunload', handleBeforeUnload);
                clearInterval(saveInterval);
                localStorage.removeItem(storageKey);
            };
        } else {
            const updateTime = () => {
                setCurrentTime(videoElement.currentTime);
            };
            const updateDuration = () => {
                setDuration(videoElement.duration);
            };
            videoElement.addEventListener('timeupdate', updateTime);
            videoElement.addEventListener('loadedmetadata', updateDuration);
            return () => {
                videoElement.removeEventListener('timeupdate', updateTime);
                videoElement.removeEventListener('loadedmetadata', updateDuration);
                localStorage.removeItem(storageKey);
            };
        }
    }, [props.videoId, props.source, props.resumeFromLastTime, storageKey]);
    useEffect(() => {
        const videoElement = videoRef.current;
        if (isPlaying) {
            const userPlayEvent = new CustomEvent('sxm-media-play', { bubbles: true });
            videoElement?.dispatchEvent(userPlayEvent);
        } else if (isInitiated && !isPlaying) {
            const userPauseEvent = new CustomEvent('sxm-media-pause');
            videoElement?.dispatchEvent(userPauseEvent);
        }
    }, [isPlaying, isInitiated]);
    const togglePlayPause = () => {
        const videoElement = videoRef.current;
        if (!videoElement) return;
        if (isPlaying) {
            userPausedRef.current = true;
            videoElement.pause();
            clearInitialPlayFallbackTimeout();
            setControlBarOpen(true);
            setIsPlaying(false);
        } else {
            userPausedRef.current = false;
            videoElement.play();
            setControlBarOpen(false);
            if (!isInitiated) setIsInitiated(true);
            setIsPlaying(true);
        }
    };
    const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const videoElement = videoRef.current;
        const volumeValue = Number(event.target.value);
        if (videoElement) {
            videoElement.volume = volumeValue;
        }
        setVolume(volumeValue);
    };
    const handleTimingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const videoElement = videoRef.current;
        const seekTime = Number(event.target.value);
        if (videoElement) {
            videoElement.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };
    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };
    const handleFullScreen = () => {
        if (videoRef.current) {
            if ((videoRef.current as SafariVideoElement).webkitEnterFullscreen) {
                (videoRef.current as SafariVideoElement).webkitEnterFullscreen?.();
            }
            if (videoRef.current.requestFullscreen) {
                videoRef.current.requestFullscreen();
            }
        }
    };
    const toggleTranscriptModal = () => {
        const next = !isTranscriptOpen;
        setIsTranscriptOpen(next);
        if (!next && props.onTimeSync) {
            if (props.videoId) {
                props.onTimeSync(getCurrentYouTubeTime());
            } else if (videoRef.current) {
                props.onTimeSync(videoRef.current.currentTime);
            }
        }
        props.onTranscriptToggle?.(next);
    };
    const isTranscriptAvailable = props.transcript && isTranscriptOpen;
    const videoContentStyles = cn(styles.videoContent, {
        [styles.positionTranscriptRight]: isTranscriptAvailable,
        [styles.inlineVideoContent]: props.isInlineVideo,
    });
    const transcriptWrapperStyles = cn(styles.transcriptWrapper, {
        [styles.transcriptFloatRight]: isTranscriptAvailable,
    });
    const videoControlsContainerStyles = cn(styles.videoControlsContainer, {
        [styles.shortControlBar]: isTranscriptAvailable,
    });
    const handleTranscriptClosing = () => {
        setIsTranscriptOpen(false);
        props.onTranscriptToggle?.(false);
    };
    const handleVolumeClick = () => {
        const videoElement = videoRef.current;
        if (videoElement) {
            if (volume !== 0) {
                videoElement.volume = 0;
                setPreviousVolumeLevel(volume);
                setVolume(0);
            } else {
                videoElement.volume = previousVolumeLevel;
                setVolume(previousVolumeLevel);
            }
        }
    };
    const youtubeElementId = useMemo(() => {
        if (!props.videoId) {
            return '';
        }
        return `youtube-player-${generatedId.replace(/:/g, '')}`;
    }, [generatedId, props.videoId]);
    const [ccEnabled, setCcEnabled] = useState(true);
    const setTrackMode = (enabled: boolean) => {
        const video = videoRef.current;
        if (!video) return;
        const tracks = video.textTracks;
        if (!tracks || tracks.length === 0) return;
        const track = Array.from(tracks).find((t) => t.kind === 'captions');
        if (!track) return;
        track.mode = enabled ? 'showing' : 'hidden';
        setCcEnabled(enabled);
    };
    useEffect(() => {
        if (!isPlaying) return;
        let preferredEnabled = true;
        try {
            const stored = localStorage.getItem(SESSION_CC_KEY);
            if (stored !== null) {
                preferredEnabled = stored === 'true';
            }
        } catch (e) {
            console.error(e);
        }
        setTrackMode(preferredEnabled);
    }, [isPlaying]);
    const toggleCC = () => {
        const next = !ccEnabled;
        setTrackMode(next);
        try {
            localStorage.setItem(SESSION_CC_KEY, String(next));
        } catch (e) {
            console.error(e);
        }
    };
    useEffect(() => {
        onPlayingChange?.(isPlaying);
    }, [isPlaying, onPlayingChange]);
    return (
        <>
            {props.source && isSourceVideo ? (
                <div className={videoModalClassNames} data-componenttype={props.dataComponentType}>
                    <div className={videoContentStyles}>
                        {isLoading && <VideoSpinnerLoader />}
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions are conditionally rendered via captionSrc prop when available from CMS. */}
                        <video
                            ref={videoRef}
                            playsInline
                            poster={props.poster}
                            aria-describedby={generatedId}
                            preload="none"
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                        >
                            <source src={props.source} type="video/mp4" />
                            {props.captionSrc && (
                                <track src={props.captionSrc} kind="captions" srcLang="en" label="English" default />
                            )}
                        </video>
                    </div>
                    <div className={videoControlsContainerStyles}>
                        <div
                            className={styles.playPauseToggleOverlay}
                            role="presentation"
                            onClick={togglePlayPause}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        />
                        <div className={styles.videoTiming}>
                            <div className={styles.badgeTiming}>
                                <div className={styles.currentTime}>
                                    <span className={styles.timeDisplay}>{formatTime(currentTime)}</span>
                                </div>
                                <div className={styles.remainingTime}>
                                    <span className={styles.timeDisplay}>-{formatTime(duration - currentTime)}</span>
                                </div>
                            </div>
                            <input
                                type="range"
                                className={styles.timeProgressBar}
                                min="0"
                                max={duration}
                                step="0.1"
                                value={currentTime}
                                onChange={handleTimingChange}
                                style={{
                                    background: `linear-gradient(to right, #ffffff ${(currentTime / duration) * 100}%, rgba(255, 255, 255, 0.20) ${(currentTime / duration) * 100}%)`,
                                }}
                            />
                        </div>
                        <div className={styles.controllerContainer}>
                            <div
                                className={cn(styles.videoTitleContainer, {
                                    [styles.fullSizePoster]: props.fullSizePoster,
                                })}
                            >
                                {props.description ? (
                                    <Text tag="p" className={styles.videoTitle}>
                                        {props.description}
                                    </Text>
                                ) : null}
                            </div>
                            <div className={styles.playPauseContainer}>
                                <ButtonWithLabel
                                    className={styles.playPauseButton}
                                    onClick={togglePlayPause}
                                    labelText={isPlaying ? 'Pause' : 'Play'}
                                    ariaPressed={!isPlaying}
                                    doNotTrack={true}
                                >
                                    {isPlaying ? <IconVideoPauseButton /> : <IconVideoPlayButton />}
                                </ButtonWithLabel>
                            </div>
                            <div className={styles.rightContainer}>
                                {props.captionSrc && (
                                    <div className={styles.ccButtonContainer}>
                                        <ButtonWithLabel
                                            className={styles.captionButton}
                                            onClick={toggleCC}
                                            labelText={`Closed captions ${ccEnabled ? 'On' : 'Off'}`}
                                            ariaPressed={ccEnabled}
                                        >
                                            {ccEnabled ? <IconClosedCaptionActive /> : <IconClosedCaption />}
                                        </ButtonWithLabel>
                                    </div>
                                )}
                                {props.transcript && (
                                    <div className={styles.ccButtonContainer}>
                                        <ButtonWithLabel
                                            className={styles.captionButton}
                                            onClick={toggleTranscriptModal}
                                            labelText={`Transcript ${isTranscriptOpen ? 'On' : 'Off'}`}
                                            ariaPressed={isTranscriptOpen}
                                        >
                                            {isTranscriptOpen ? <IconTranscriptActive /> : <IconTranscript />}
                                        </ButtonWithLabel>
                                    </div>
                                )}
                                <div className={styles.innerRightContainer}>
                                    <div className={styles.volumeContainer}>
                                        <ButtonWithLabel
                                            className={styles.volumeButton}
                                            onClick={handleVolumeClick}
                                            labelText={volume === 0 ? 'Unmute' : 'Mute'}
                                            ariaPressed={volume === 0}
                                        >
                                            {volume > 0 && volume <= 0.5 && <IconVolume />}
                                            {volume > 0.5 && <IconHighVolume />}
                                            {volume === 0 && <IconMutedVolume />}
                                        </ButtonWithLabel>
                                        <input
                                            type="range"
                                            className={styles.volumeControl}
                                            min="0"
                                            max="1"
                                            step="0.1"
                                            value={volume}
                                            onChange={handleVolumeChange}
                                            style={{
                                                background: `linear-gradient(to right, #ffffff ${volume * 100}%, rgba(255, 255, 255, 0.20) ${volume * 100}%)`,
                                            }}
                                        />
                                    </div>
                                    <ButtonWithLabel
                                        className={styles.desktopViewButton}
                                        onClick={handleFullScreen}
                                        labelText="Full screen"
                                    >
                                        <IconDesktopView />
                                    </ButtonWithLabel>
                                </div>
                            </div>
                        </div>
                    </div>
                    {isTranscriptOpen && props.transcript && (
                        <Transcript
                            classNames={transcriptWrapperStyles}
                            closeHandler={handleTranscriptClosing}
                            transcript={props.transcript}
                        />
                    )}
                </div>
            ) : (
                props.videoId &&
                props.initiatePlayer && (
                    <div className={youtubeVideoModalClassNames} data-sxmyoutubeplayer>
                        <div
                            ref={youtubeContainerRef}
                            data-video-title={props.plainTextTitle}
                            id={youtubeElementId}
                            className={styles.youtubeVideo}
                        />
                    </div>
                )
            )}
        </>
    );
}
